export interface PurchaseEvent {
  orderId?: string;
  value?: number;
  currency?: string;
  source: 'shopify' | 'dataLayer' | 'manual';
}

export class PurchaseDetector {
  private queue: PurchaseEvent[] = [];
  private trackedOrderIds = new Set<string>();
  private isIntercepting = false;
  private purchaseCallback: ((event: PurchaseEvent) => void) | null = null;

  onPurchase(callback: (event: PurchaseEvent) => void): () => void {
    this.purchaseCallback = callback;
    return () => {
      this.purchaseCallback = null;
    };
  }

  interceptDataLayerPush(): void {
    if (this.isIntercepting) return;
    this.isIntercepting = true;

    try {
      if (!(window as any).dataLayer) {
        (window as any).dataLayer = [];
      }
      const dataLayer = (window as any).dataLayer;

      // Scan existing entries for purchase events pushed before interception
      for (const entry of dataLayer) {
        this.handleDataLayerEntry(entry);
      }

      const originalPush = dataLayer.push.bind(dataLayer);

      dataLayer.push = (...args: unknown[]) => {
        const result = originalPush(...args);
        for (const arg of args) {
          this.handleDataLayerEntry(arg);
        }
        return result;
      };
    } catch (error) {
      console.debug('[Superbar] purchase detection error:', error);
    }
  }

  detectShopifyCheckout(): void {
    try {
      const checkout = (window as any).Shopify?.checkout;
      if (!checkout || typeof checkout !== 'object' || !checkout.order_id) {
        return;
      }

      const orderId = String(checkout.order_id);
      const parsedPrice = parseFloat(checkout.total_price);
      const value = Number.isFinite(parsedPrice) ? parsedPrice : undefined;

      this.enqueue({
        orderId,
        value,
        currency: checkout.currency || undefined,
        source: 'shopify',
      });
    } catch (error) {
      console.debug('[Superbar] purchase detection error:', error);
    }
  }

  start(platformType?: string): void {
    if (platformType === 'shopify') {
      this.detectShopifyCheckout();
    }
    this.interceptDataLayerPush();
  }

  registerManualPurchase(data: Omit<PurchaseEvent, 'source'>): void {
    this.enqueue({
      orderId: data.orderId,
      value: data.value,
      currency: data.currency,
      source: 'manual',
    });
  }

  markOrderIdTracked(orderId: string): void {
    this.trackedOrderIds.add(orderId);
  }

  drainQueue(): PurchaseEvent[] {
    const events = [...this.queue];
    this.queue = [];
    return events;
  }

  private enqueue(event: PurchaseEvent): void {
    const dedupKey = event.orderId;
    if (dedupKey && this.trackedOrderIds.has(dedupKey)) return;
    if (dedupKey) this.trackedOrderIds.add(dedupKey);

    this.queue.push(event);
    this.purchaseCallback?.(event);
  }

  private handleDataLayerEntry(entry: unknown): void {
    try {
      if (
        !entry ||
        typeof entry !== 'object' ||
        (entry as any).event !== 'purchase'
      ) {
        return;
      }

      const ecommerce = (entry as any).ecommerce;

      this.enqueue({
        orderId: ecommerce?.transaction_id
          ? String(ecommerce.transaction_id)
          : undefined,
        value:
          typeof ecommerce?.value === 'number' ? ecommerce.value : undefined,
        currency: ecommerce?.currency || undefined,
        source: 'dataLayer',
      });
    } catch (error) {
      console.debug('[Superbar] purchase detection error:', error);
    }
  }
}

export const purchaseDetector = new PurchaseDetector();
